// Pure helpers for conversion tracking (no DOM access): campaign attribution, GTM events,
// thank-you redirects and the lead e-mail subject. Browser side lives in lib/lead-tracking-client.ts.

export const ATTRIBUTION_KEYS = [
  'gclid',
  'gbraid',
  'wbraid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];
export type Attribution = Partial<Record<AttributionKey, string>>;

export type LeadLocale = 'es' | 'en';

/** Stored attribution: campaign params + first external referrer + landing path + capture time. */
export type AttributionRecord = Attribution & { referrer?: string; landing?: string; ts: number };

/** ADS | SEO | SOCIAL | REF | DIRECTO */
export type Origen = 'ADS' | 'SEO' | 'SOCIAL' | 'REF' | 'DIRECTO';

/** Extra fields every lead form sends to /api/mail (hidden fields + context). */
export type LeadTrackingPayload = {
  servicio: string;
  attribution: Attribution;
  pagePath: string;
  landingPage: string;
  origen: Origen;
  /** Same format as the WhatsApp "Ref:" code, e.g. ADS-NOMADA-7K2F. */
  refId: string;
};

/** 90 days: Google Ads conversion window. */
export const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;

const OWN_HOSTS = /(^|\.)migrationlaw\.es$/;

const MAX_VALUE_LENGTH = 200;
const SLUG_FALLBACK = 'consulta-general';

function cleanValue(value: unknown): string {
  if (typeof value !== 'string') return '';
  // Strip control characters and cap length: values end up in e-mails and Cosmos documents.
  return value.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, MAX_VALUE_LENGTH);
}

/** Reads gclid / gbraid / wbraid / utm_* from a query string. */
export function parseAttribution(search: string): Attribution {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const result: Attribution = {};
  for (const key of ATTRIBUTION_KEYS) {
    const value = cleanValue(params.get(key));
    if (value) result[key] = value;
  }
  return result;
}

/** Last-click merge: a new campaign visit replaces the stored one; otherwise the stored values are kept. */
export function mergeAttribution(stored: Attribution, fresh: Attribution): Attribution {
  return Object.keys(fresh).length > 0 ? { ...fresh } : { ...stored };
}

/** Whitelists and sanitizes attribution coming from the client (server side). */
export function sanitizeAttribution(input: unknown): Attribution {
  if (!input || typeof input !== 'object') return {};
  const record = input as Record<string, unknown>;
  const result: Attribution = {};
  for (const key of ATTRIBUTION_KEYS) {
    const value = cleanValue(record[key]);
    if (value) result[key] = value;
  }
  return result;
}

export function sanitizeTrackingText(value: unknown): string {
  return cleanValue(value);
}

function externalReferrer(referrer: string | undefined): string | undefined {
  if (!referrer) return undefined;
  try {
    const host = new URL(referrer).hostname;
    return OWN_HOSTS.test(host) || host === 'localhost' ? undefined : cleanValue(referrer).slice(0, 200);
  } catch {
    return undefined;
  }
}

/**
 * Decides what to store on each page view: a new ad/campaign click replaces the stored record;
 * otherwise the first touch is kept until it expires (90 days).
 */
export function resolveAttributionRecord(
  stored: AttributionRecord | null,
  visit: { search: string; referrer?: string; pathname: string; now: number },
): AttributionRecord {
  const fresh = parseAttribution(visit.search);
  const valid = stored && visit.now - stored.ts <= ATTRIBUTION_TTL_MS ? stored : null;
  if (valid && Object.keys(fresh).length === 0) return valid;
  return {
    ...fresh,
    referrer: externalReferrer(visit.referrer),
    landing: cleanValue(visit.pathname),
    ts: visit.now,
  };
}

/** Classifies the visit source for the WhatsApp/lead reference code. */
export function getOrigen(record: Partial<AttributionRecord> | null | undefined): Origen {
  if (!record) return 'DIRECTO';
  if (record.gclid || record.gbraid || record.wbraid) return 'ADS';
  const source = (record.utm_source || '').toLowerCase();
  const medium = (record.utm_medium || '').toLowerCase();
  const referrer = externalReferrer(record.referrer) || '';
  const host = referrerHost(referrer);
  if (medium === 'cpc' || medium === 'ppc' || source === 'google_ads') return 'ADS';
  if (/facebook|instagram|linkedin|tiktok|twitter/.test(source) || SOCIAL_HOSTS.test(host)) return 'SOCIAL';
  if (SEARCH_HOSTS.test(host) && !NON_SEARCH_SUBDOMAINS.test(host)) return 'SEO';
  if (referrer) return 'REF';
  return 'DIRECTO';
}

// Match whole hostnames: substring checks misclassified dropbox.com as x.com or Gmail as Google search
const SOCIAL_HOSTS = /(^|\.)(facebook\.com|fb\.com|instagram\.com|linkedin\.com|lnkd\.in|tiktok\.com|x\.com|twitter\.com|t\.co)$/;
const SEARCH_HOSTS = /(^|\.)(google\.[a-z.]+|bing\.com|duckduckgo\.com|yahoo\.com|ecosia\.org)$/;
const NON_SEARCH_SUBDOMAINS = /^(mail|docs|drive|accounts)\./;

function referrerHost(referrer: string): string {
  try {
    return new URL(referrer).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return '';
  }
}

// Códigos cortos por servicio para el "Ref:" de WhatsApp y de los leads
const SERVICE_REF_CODES: Record<string, string> = {
  'visado-nomada-digital': 'NOMADA',
  'visado-de-nomada-digital': 'NOMADA',
  'ley-beckham': 'BECKHAM',
  'pack-nomada-digital-ley-beckham': 'PACK',
  'residencia-no-lucrativa': 'NOLUCRATIVA',
  'visado-para-profesionales-altamente-cualificados': 'PAC',
  'visado-alta-cualificacion': 'PAC',
  'asesoria-legal': 'ASESORIA',
  'renovacion-regularizacion': 'RENOVACION',
  'renovar-permiso-regularizacion-2026': 'RENOVACION',
  'renovacion-residencia': 'RENOVACIONRES',
  'nacionalidad-espanola': 'NACIONALIDAD',
  'arraigo': 'ARRAIGO',
  'residencia-por-estudios': 'ESTUDIOS',
  'fiscalidad-internacional': 'FISCALIDAD',
};

export function getServiceRefCode(servicio: string | null | undefined): string {
  const slug = toServicioSlug(servicio);
  if (SERVICE_REF_CODES[slug]) return SERVICE_REF_CODES[slug];
  if (slug.startsWith('arraigo')) return 'ARRAIGO';
  return 'GENERAL';
}

const SHORT_ID_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // sin 0/O ni 1/I

/** Short readable code; uses crypto.getRandomValues (browser and Node 20+). */
export function shortId(length = 4, random: (values: Uint32Array) => Uint32Array = (values) => globalThis.crypto.getRandomValues(values)): string {
  return Array.from(random(new Uint32Array(length)), (n) => SHORT_ID_ALPHABET[n % SHORT_ID_ALPHABET.length]).join('');
}

export function buildRefId(origen: Origen, servicio: string, id = shortId()): string {
  return `${origen}-${getServiceRefCode(servicio)}-${id}`;
}

const REF_ID_PATTERN = /^(ADS|SEO|SOCIAL|REF|DIRECTO)-[A-Z]{2,16}-[A-Z0-9]{4}$/;

/** Server-side validation of a client-sent reference code. */
export function sanitizeRefId(value: unknown): string {
  const ref = cleanValue(value).toUpperCase();
  return REF_ID_PATTERN.test(ref) ? ref : '';
}

export function sanitizeOrigen(value: unknown): Origen | '' {
  const origen = cleanValue(value).toUpperCase();
  return (['ADS', 'SEO', 'SOCIAL', 'REF', 'DIRECTO'] as const).find((item) => item === origen) ?? '';
}

/** Normalizes any service identifier to a URL-safe slug for ?servicio=. */
export function toServicioSlug(value: string | null | undefined): string {
  const slug = (value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/^\/+|\/+$/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || SLUG_FALLBACK;
}

/** Thank-you URL watched by the GTM conversion trigger (any path containing /contacto/gracias). */
export function buildThankYouUrl(locale: LeadLocale, servicio: string): string {
  const base = locale === 'en' ? '/en/contacto/gracias' : '/contacto/gracias';
  return `${base}?servicio=${encodeURIComponent(toServicioSlug(servicio))}`;
}

export function buildGenerateLeadEvent(servicio: string, locale: LeadLocale) {
  return { event: 'generate_lead', servicio: toServicioSlug(servicio), idioma: locale } as const;
}

/** `whatsapp_click` keeps its name (GTM listens to it); ref_id/origen/servicio code are extra params. */
export function buildWhatsAppClickEvent(params: {
  pagePath: string;
  servicio: string;
  refId?: string;
  origen?: Origen;
  utmCampaign?: string;
}) {
  return {
    event: 'whatsapp_click',
    page_path: params.pagePath,
    servicio: getServiceRefCode(params.servicio),
    servicio_slug: toServicioSlug(params.servicio),
    ref_id: params.refId,
    origen: params.origen,
    utm_campaign: params.utmCampaign,
  } as const;
}

export function buildPhoneClickEvent(pagePath: string) {
  return { event: 'phone_click', page_path: pagePath } as const;
}

/** Admin e-mail subject: "[Lead] <servicio> – <país> – <nombre>". */
export function buildLeadSubject(params: { servicio: string; country?: string; name: string }): string {
  const parts = [params.servicio, params.country || 'País no indicado', params.name].map((part) =>
    cleanValue(part).replace(/[\r\n]+/g, ' ') || '-',
  );
  return `[Lead] ${parts.join(' – ')}`;
}

/** Plain-text lines with campaign data for the admin e-mail. */
export function formatAttributionLines(attribution: Attribution): string[] {
  const lines = ATTRIBUTION_KEYS.filter((key) => attribution[key]).map((key) => `${key}: ${attribution[key]}`);
  return lines.length > 0 ? lines : ['Sin datos de campaña (tráfico directo u orgánico)'];
}
